import { api, ApiError, uploadPhoto, ALLOWED_IMAGE_TYPES } from "../api.js";
import { esc, linkify, cohortIcons, avatarHtml, displayName, timeAgo, toast } from "../utils.js";
import { getCurrentUser, isSiteAdmin } from "../auth.js";
import { navigate } from "../app.js";
import { confirmAction } from "../confirmDialog.js";
import { reportDialog } from "../reportDialog.js";
import { followButtonHtml, attachFollowHandlers } from "../followButton.js";
import { reactionsBarHtml, attachReactionHandlers } from "../reactions.js";
import { manageMenuHtml } from "../manageMenu.js";
import { unitsOf } from "../units.js";
import { diveProfileHtml, attachDiveProfiles, attachDivePointer } from "../diveProfileChart.js";

const MAX_PHOTOS = 10; // mirrors _MAX_PHOTOS_PER_ITEM in app/posts/routes.py

/** Photos attached to a comment. Smaller than the post gallery so a
 * photo-heavy thread stays readable; each opens its photo page. */
function commentPhotosHtml(photos) {
  if (!photos || photos.length === 0) return "";
  const items = photos
    .map(
      (photo) => `
      <a href="#/photo/${photo.id}" aria-label="Open this photo">
        <img src="${esc(photo.image_url)}" alt="" loading="lazy"
             class="rounded border" style="width:110px;height:110px;object-fit:cover;" />
      </a>`
    )
    .join("");
  return `<div class="d-flex flex-wrap gap-2 mt-2">${items}</div>`;
}

/** True when the signed-in user may remove this comment: its author, or
 * a site admin. Mirrors the API's rule — the button is a convenience, the
 * server is the authority. */
function canDeleteComment(comment) {
  const me = getCurrentUser();
  if (me == null) return false;
  return (comment.author && me.id === comment.author.id) || isSiteAdmin();
}

function commentHtml(comment, postId) {
  const author = displayName(comment.author);
  const mine = getCurrentUser() != null && comment.author && getCurrentUser().id === comment.author.id;
  const control = canDeleteComment(comment)
    ? `<button type="button" class="btn btn-sm btn-link text-danger p-0 ms-2 comment-delete"
               data-comment-id="${comment.id}"
               title="${mine ? "Delete your comment" : "Remove this comment"}"
               aria-label="${mine ? "Delete your comment" : "Remove this comment"}">
         <i class="bi bi-trash"></i></button>`
    : "";
  const reportControl = !mine && getCurrentUser() != null
    ? `<button type="button" class="btn btn-sm btn-link text-slate p-0 ms-2 comment-report"
               data-comment-id="${comment.id}" title="Report this comment"
               aria-label="Report this comment"><i class="bi bi-flag"></i></button>`
    : "";
  return `
    <div class="d-flex mb-3 p-2 rounded" data-comment-row="${comment.id}">
      ${avatarHtml(author, { className: "me-2", sizePx: 32, fontSize: "0.8rem" })}
      <div class="flex-grow-1">
        <div>
          <span class="fw-semibold">${esc(author)}</span>
          <span class="text-slate small ms-1">${timeAgo(comment.created_at)}</span>
          ${control}${reportControl}
        </div>
        ${comment.content ? `<div class="dc-user-text">${linkify(comment.content)}</div>` : ""}
        ${commentPhotosHtml(comment.photos)}
        <div class="mt-2 mb-2">${reactionsBarHtml({ postId, commentId: comment.id }, comment.reactions)}</div>
      </div>
    </div>`;
}

/** Full-size gallery for the detail view — unlike the feed's capped
 * thumbnail strip, every photo on the post is shown. Each opens the
 * original in a new tab. */
/** The post's photos.
 *
 * Each opens its photo page rather than the raw image in a new tab: the
 * page shows the picture full size *and* who uploaded it, when, and what
 * it is tagged with — and it keeps the reader inside the app. Plain
 * anchors, so middle-click and "open in new tab" still work for anyone
 * who wants the old behaviour. */
function galleryHtml(photos) {
  if (!photos || photos.length === 0) return "";
  const items = photos
    .map(
      (photo) => `
      <a href="#/photo/${photo.id}" class="d-block" style="max-width:220px;"
         aria-label="Open this photo">
        <img src="${esc(photo.image_url)}" alt="" loading="lazy"
             class="rounded border w-100" style="max-height:220px;object-fit:cover;" />
      </a>`
    )
    .join("");
  return `<div class="d-flex flex-wrap gap-2">${items}</div>`;
}

export async function render(container, params, query) {
  const postId = params.id;
  container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;

  let post;
  try {
    post = await api.get(`/posts/${postId}`);
  } catch (err) {
    container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Could not load this post.</div></div>`;
    return;
  }

  function isAuthor() {
    const me = getCurrentUser();
    return me != null && post.author != null && me.id === post.author.id;
  }

  async function deletePost() {
    const mine = isAuthor();
    const ok = await confirmAction({
      title: mine ? "Delete this post?" : "Remove this post?",
      body: mine
        ? "This removes the post and all of its comments. It can't be undone."
        : `You are removing ${displayName(post.author)}'s post as a site administrator. `
          + "This removes the post and all of its comments, and can't be undone.",
      detail: post.content,
      confirmLabel: mine ? "Delete" : "Remove post",
    });
    if (!ok) return;
    try {
      await api.delete(`/posts/${postId}`);
      navigate("/feed");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not delete this post.", "danger");
    }
  }

  /** Sends a report and reports back. `target` is {post_id} or {comment_id}. */
  async function submitReport(target, what, snippet) {
    const answer = await reportDialog({ what, snippet });
    if (!answer) return;
    try {
      await api.post("/reports", { ...target, ...answer });
      // Same message whether this was new or a duplicate — the API is
      // idempotent, and "you already reported this" only invites retrying.
      toast("Thanks — that's been sent to the moderators.", "success");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not send that report.", "danger");
    }
  }

  function renderAll() {
    const author = displayName(post.author);
    const chips = [];
    // The menu is now useful to any signed-in viewer: the owner edits or
    // deletes, a moderator removes, everyone else reports.
    const canEdit = isAuthor();
    const canDelete = isAuthor() || isSiteAdmin();
    const canReport = !isAuthor() && getCurrentUser() != null;

    if (post.dive_plan) {
      const statusBadge = post.dive_plan.is_past
        ? `<span class="badge text-bg-secondary me-2">Past dive</span>`
        : `<span class="badge text-bg-primary me-2">Upcoming dive</span>`;
      chips.push(
        `<a href="#/dive-plan/${post.dive_plan.id}" class="badge text-bg-light border me-2 text-decoration-none"><i class="bi bi-calendar-event me-1"></i>${esc(post.dive_plan.title)}</a>${statusBadge}`
      );
    }
    if (post.dive_site) {
      chips.push(
        `<a href="#/dive-site/${post.dive_site.id}" class="badge text-bg-light border me-2 text-decoration-none"><i class="bi bi-geo-alt me-1"></i>${esc(post.dive_site.name)}</a>`
      );
    }
    if (post.community) {
      chips.push(
        `<a href="#/cohort/${post.community.id}" class="badge text-bg-light border me-2 text-decoration-none">${cohortIcons(post.community)}${esc(post.community.name)}</a>`
      );
    }

    container.innerHTML = `
    <button class="btn btn-link ps-0 mb-2" id="back-btn"><i class="bi bi-arrow-left"></i> Back</button>
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          ${avatarHtml(author, { className: "me-2" })}
          <div>
            <div class="fw-semibold">${esc(author)}</div>
            <div class="text-slate small">${timeAgo(post.created_at)}</div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <!-- No ms-auto here: this row already spaces its children,
                 unlike the post card where the button pushes right. -->
            ${followButtonHtml(post.author, { extraClass: "" })}
            ${manageMenuHtml(
              [
                canEdit && { id: "edit-post-btn", icon: "bi-pencil", label: "Edit" },
                canReport && { id: "report-post-btn", icon: "bi-flag", label: "Report" },
                canDelete && {
                  id: "delete-post-btn",
                  icon: "bi-trash",
                  label: isAuthor() ? "Delete" : "Remove as moderator",
                  danger: true,
                },
              ],
              { label: "Manage this post" }
            )}
          </div>
        </div>
        ${chips.length ? `<div class="mb-3">${chips.join("")}</div>` : ""}
        <p class="fs-5 dc-user-text ${post.photos?.length || post.dive_profile ? "mb-3" : "mb-0"}">${linkify(post.content)}</p>
        ${post.dive_profile ? diveProfileHtml(post.dive_profile, { units: unitsOf(getCurrentUser()), size: "large" }) : ""}
        ${galleryHtml(post.photos)}
        <div class="mt-3 mb-2">${reactionsBarHtml({ postId: post.id }, post.reactions)}</div>
      </div>
    </div>
    <h2 class="h5 mb-3">Comments</h2>
    <div id="comments-list"></div>
    <form id="comment-form" class="mt-3">
      <div class="d-flex gap-2">
        <textarea class="form-control" id="comment-input" rows="1"
                  placeholder="Add a comment or a photo..."
                  style="resize:none;overflow:hidden;"></textarea>
        <label class="btn btn-outline-secondary mb-0" for="comment-photos" title="Attach photos">
          <i class="bi bi-image"></i>
        </label>
        <input type="file" id="comment-photos" class="d-none"
               accept="${ALLOWED_IMAGE_TYPES.join(",")}" multiple />
        <button type="submit" class="btn btn-primary" id="comment-submit"><i class="bi bi-send"></i></button>
      </div>
      <div id="comment-previews" class="d-flex flex-wrap gap-2 mt-2"></div>
    </form>`;

    container.querySelector("#back-btn").addEventListener("click", () => history.back());
    // Re-bound on every renderAll(), since the header is rewritten.
    if (post.dive_profile) {
      attachDiveProfiles(container);
      attachDivePointer(container.querySelector(".dive-profile"), post.dive_profile);
    }
    attachFollowHandlers(container);
    attachReactionHandlers(container);

    const editBtn = container.querySelector("#edit-post-btn");
    if (editBtn) editBtn.addEventListener("click", () => navigate(`/post/${postId}/edit`));
    const deleteBtn = container.querySelector("#delete-post-btn");
    if (deleteBtn) deleteBtn.addEventListener("click", deletePost);
    const reportBtn = container.querySelector("#report-post-btn");
    if (reportBtn) {
      reportBtn.addEventListener("click", () =>
        submitReport({ post_id: Number(postId) }, "post", post.content)
      );
    }

    const commentsList = container.querySelector("#comments-list");

    async function deleteComment(commentId, comment) {
      const me = getCurrentUser();
      const mine = me != null && comment.author && me.id === comment.author.id;
      const ok = await confirmAction({
        title: mine ? "Delete your comment?" : "Remove this comment?",
        body: mine
          ? "This can't be undone."
          : `You are removing ${displayName(comment.author)}'s comment as a site administrator. `
            + "This can't be undone.",
        detail: comment.content || (comment.photos?.length ? "(photo only)" : ""),
        confirmLabel: mine ? "Delete" : "Remove comment",
      });
      if (!ok) return;
      try {
        await api.delete(`/posts/${postId}/comments/${commentId}`);
        const refreshed = await api.get(`/posts/${postId}`);
        renderComments(refreshed.comments || []);
        toast("Comment removed.", "success");
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Could not remove that comment.", "danger");
      }
    }

    function renderComments(comments) {
      commentsList.innerHTML = comments.length
        ? comments.map((c) => commentHtml(c, Number(postId))).join("")
        : `<p class="text-slate">No comments yet — say something!</p>`;
      // Re-bound on every render, since the list is replaced wholesale.
      attachReactionHandlers(commentsList);
      commentsList.querySelectorAll(".comment-delete").forEach((btn) => {
        const id = Number(btn.dataset.commentId);
        const comment = comments.find((c) => c.id === id);
        if (comment) btn.addEventListener("click", () => deleteComment(id, comment));
      });
      commentsList.querySelectorAll(".comment-report").forEach((btn) => {
        const id = Number(btn.dataset.commentId);
        const comment = comments.find((c) => c.id === id);
        if (comment) {
          btn.addEventListener("click", () =>
            submitReport({ comment_id: id }, "comment",
              comment.content || (comment.photos?.length ? "(photo only)" : ""))
          );
        }
      });
    }
    renderComments(post.comments || []);

    // Arriving from the moderation queue with ?comment=<id>: put the
    // reported comment on screen and mark it, so a moderator isn't left
    // scanning a long thread for the one they came to deal with.
    const focusId = query?.get("comment");
    if (focusId) {
      const row = commentsList.querySelector(`[data-comment-row="${CSS.escape(focusId)}"]`);
      if (row) {
        row.classList.add("dc-flagged");
        row.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        toast("That comment is no longer here — it may have been deleted.", "warning");
      }
    }

    // Files chosen but not yet uploaded — same two-stage flow as the post
    // composer: nothing is sent to the API until every object key is in hand.
    const pendingFiles = [];
    const photoInput = container.querySelector("#comment-photos");
    const previewsEl = container.querySelector("#comment-previews");

    function releasePreviews() {
      pendingFiles.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    }
    window.addEventListener("hashchange", releasePreviews, { once: true });

    function renderPreviews() {
      previewsEl.innerHTML = pendingFiles
        .map(
          (p, i) => `
          <div class="position-relative" style="width:72px;height:72px;">
            <img src="${esc(p.previewUrl)}" alt="${esc(p.file.name)}"
                 class="rounded border w-100 h-100" style="object-fit:cover;" />
            <button type="button"
                    class="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 lh-1 rounded-circle"
                    style="width:18px;height:18px;" data-remove-index="${i}"
                    aria-label="Remove ${esc(p.file.name)}">&times;</button>
          </div>`
        )
        .join("");
      previewsEl.querySelectorAll("[data-remove-index]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const i = Number(btn.dataset.removeIndex);
          URL.revokeObjectURL(pendingFiles[i].previewUrl);
          pendingFiles.splice(i, 1);
          renderPreviews();
        });
      });
    }

    photoInput.addEventListener("change", () => {
      const room = MAX_PHOTOS - pendingFiles.length;
      const chosen = Array.from(photoInput.files);
      chosen.slice(0, Math.max(room, 0)).forEach((file) => {
        pendingFiles.push({ file, previewUrl: URL.createObjectURL(file) });
      });
      if (chosen.length > room) {
        toast(`A comment can have at most ${MAX_PHOTOS} photos — extra files were skipped.`, "warning");
      }
      photoInput.value = ""; // so re-picking the same file fires `change`
      renderPreviews();
    });

    const commentInput = container.querySelector("#comment-input");

    // Grow with the text instead of scrolling inside one row. Guarded
    // because scrollHeight is 0 where there is no layout engine.
    const MAX_INPUT_PX = 160;
    function autoGrow() {
      commentInput.style.height = "auto";
      if (commentInput.scrollHeight) {
        commentInput.style.height = `${Math.min(commentInput.scrollHeight, MAX_INPUT_PX)}px`;
        commentInput.style.overflowY =
          commentInput.scrollHeight > MAX_INPUT_PX ? "auto" : "hidden";
      }
    }
    commentInput.addEventListener("input", autoGrow);

    commentInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        container.querySelector("#comment-form").requestSubmit();
      }
    });

    container.querySelector("#comment-form").addEventListener("submit", async (e) => {
      e.preventDefault();
      const input = container.querySelector("#comment-input");
      const submitBtn = container.querySelector("#comment-submit");
      const text = input.value.trim();
      // Either kind of content is enough — a photo with no caption posts.
      if (!text && pendingFiles.length === 0) return;
      submitBtn.disabled = true;
      try {
        const photoKeys = [];
        for (const pending of pendingFiles) {
          photoKeys.push(await uploadPhoto(pending.file));
        }
        const body = { content: text };
        if (photoKeys.length) body.photo_keys = photoKeys;
        await api.post(`/posts/${postId}/comments`, body);
        input.value = "";
        autoGrow();
        releasePreviews();
        pendingFiles.length = 0;
        renderPreviews();
        const refreshed = await api.get(`/posts/${postId}`);
        renderComments(refreshed.comments || []);
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Could not post comment.", "danger");
      } finally {
        submitBtn.disabled = false;
      }
    });
  }

  renderAll();
}
